import React, { useState, useRef, useEffect } from 'react';
import { Send, Zap, Mic, ArrowLeft, AlertTriangle, TrendingUp, ShieldAlert, Brain, Calendar, Phone, Mail, ArrowUpRight } from 'lucide-react';
import { AGENT_THUMBS } from './V4BriefingShared';
import { CHURN_DATA, CHURN_CHAT_RESPONSES } from '../../data/churnData';

const CTA_ICONS = {
  'Send Email': Mail, 'Send Intro': Mail, 'Send Update': Mail, 'Send Survey': Mail,
  'Schedule Call': Phone, 'Schedule QBR': Phone,
  'Escalate': ArrowUpRight, 'Check In': Phone,
  'View Usage': TrendingUp, 'View Tickets': ShieldAlert, 'View Activity': TrendingUp,
  'Prep Deck': Zap,
};

const MetricCard = ({ item, isDark }) => {
  const tp = isDark ? 'text-white' : 'text-slate-800';
  const ts = isDark ? 'text-white/50' : 'text-slate-500';
  const border = isDark ? 'border-white/[0.06]' : 'border-black/[0.06]';
  const cardBg = item.alert ? (isDark ? 'bg-red-950/15' : 'bg-red-50/40') : (isDark ? 'bg-[#0d1b2a]' : 'bg-[#f0ece4]');
  const cardBorder = item.alert ? (isDark ? 'border-red-500/15' : 'border-red-200/50') : border;
  return (
    <div className={`${cardBg} border ${cardBorder} rounded-xl px-3 py-2.5 text-center`} data-testid={`churn-metric-${item.label.toLowerCase().replace(/\s+/g, '-')}`}>
      <p className={`text-[9px] ${ts}`}>{item.label}</p>
      <p className={`text-base font-bold mt-0.5 ${item.alert ? (isDark ? 'text-red-400' : 'text-red-600') : tp}`}>{item.value}</p>
      <span className={`text-[9px] font-medium ${item.trendUp ? (isDark ? 'text-emerald-400' : 'text-emerald-600') : (isDark ? 'text-red-400' : 'text-red-500')}`}>{item.trend}</span>
    </div>
  );
};

const AccountCard = ({ account, isDark, onAction }) => {
  const tp = isDark ? 'text-white' : 'text-slate-800';
  const ts = isDark ? 'text-white/50' : 'text-slate-500';
  const border = isDark ? 'border-white/[0.06]' : 'border-black/[0.06]';
  const riskColors = {
    critical: { bg: isDark ? 'bg-red-950/20' : 'bg-red-50/60', border: isDark ? 'border-red-500/20' : 'border-red-200/60', bar: 'bg-red-500', probBg: isDark ? 'bg-red-500/10' : 'bg-red-50/80', probText: isDark ? 'text-red-400' : 'text-red-600' },
    high: { bg: isDark ? 'bg-amber-950/15' : 'bg-amber-50/40', border: isDark ? 'border-amber-500/15' : 'border-amber-200/50', bar: 'bg-amber-500', probBg: isDark ? 'bg-amber-500/10' : 'bg-amber-50/80', probText: isDark ? 'text-amber-400' : 'text-amber-600' },
    medium: { bg: isDark ? 'bg-[#0d1b2a]' : 'bg-[#f0ece4]', border: border, bar: 'bg-yellow-500', probBg: isDark ? 'bg-yellow-500/10' : 'bg-yellow-50/80', probText: isDark ? 'text-yellow-500' : 'text-yellow-600' },
  };
  const rc = riskColors[account.risk] || riskColors.medium;

  return (
    <div className={`${rc.bg} border ${rc.border} rounded-xl p-3`} data-testid={`churn-account-${account.id}`}>
      <div className="flex items-center justify-between mb-2">
        <p className={`text-[11px] font-semibold ${tp}`}>{account.name}</p>
        <span className={`text-xs font-bold ${tp}`}>${(account.arr / 1000).toFixed(0)}K</span>
      </div>

      <div className="mb-2">
        <div className="flex items-center justify-between mb-1">
          <span className={`text-[9px] ${ts}`}>Health Score</span>
          <span className={`text-[10px] font-bold ${account.healthScore < 50 ? (isDark ? 'text-red-400' : 'text-red-500') : account.healthScore < 65 ? (isDark ? 'text-amber-400' : 'text-amber-500') : tp}`}>{account.healthScore}/100</span>
        </div>
        <div className={`w-full h-1.5 rounded-full ${isDark ? 'bg-white/[0.06]' : 'bg-black/[0.06]'}`}>
          <div className={`h-full rounded-full ${rc.bar} transition-all duration-500`} style={{ width: `${account.healthScore}%` }} />
        </div>
      </div>

      <div className={`text-[10px] leading-relaxed ${ts} mb-1`}>
        <AlertTriangle className={`w-3 h-3 inline mr-1 ${isDark ? 'text-amber-400' : 'text-amber-500'}`} />
        {account.signal}
      </div>
      <div className={`text-[10px] leading-relaxed ${isDark ? 'text-indigo-400/80' : 'text-indigo-600'} mb-2`}>
        {account.action}
      </div>

      {account.prediction && (
        <div className={`pt-2 border-t border-dashed mb-2`} style={{ borderColor: isDark ? 'rgba(255,255,255,0.06)' : 'rgba(0,0,0,0.06)' }}>
          <div className="flex items-center gap-1.5 mb-1.5">
            <Brain className={`w-3 h-3 ${isDark ? 'text-indigo-400' : 'text-indigo-500'}`} />
            <span className={`text-[9px] font-semibold ${isDark ? 'text-indigo-400' : 'text-indigo-600'}`}>Zira's Prediction</span>
          </div>
          <div className="flex items-center gap-2 mb-1">
            <span className={`text-[10px] font-bold px-2 py-0.5 rounded-full ${rc.probBg} ${rc.probText}`}>{account.prediction.churnProb}%</span>
            <span className={`text-[9px] flex items-center gap-1 ${ts}`}><Calendar className="w-3 h-3" /> {account.prediction.churnWindow}</span>
          </div>
          <p className={`text-[9px] leading-relaxed ${ts}`}>{account.prediction.reasoning}</p>
        </div>
      )}

      <div className="flex items-center justify-between">
        <span className={`text-[9px] ${ts}`}>Last login: {account.daysSinceLogin === 0 ? 'Today' : `${account.daysSinceLogin}d ago`}</span>
      </div>

      {/* CTAs */}
      {account.ctas && account.ctas.length > 0 && (
        <div className="flex items-center gap-1.5 mt-2 pt-2 border-t border-dashed" style={{ borderColor: isDark ? 'rgba(255,255,255,0.06)' : 'rgba(0,0,0,0.06)' }}>
          {account.ctas.map(cta => {
            const Icon = CTA_ICONS[cta] || Zap;
            const isEscalate = cta === 'Escalate';
            return (
              <button key={cta} onClick={() => onAction?.(account, cta)}
                className={`flex items-center gap-1 px-2 py-1 rounded-md text-[9px] font-medium transition-colors whitespace-nowrap ${
                  isEscalate ? 'bg-amber-500/10 text-amber-400 hover:bg-amber-500/20' :
                  `${isDark ? 'bg-indigo-500/10 text-indigo-400 hover:bg-indigo-500/20' : 'bg-indigo-50 text-indigo-600 hover:bg-indigo-100'}`
                }`}
                data-testid={`churn-cta-${account.id}-${cta.toLowerCase().replace(/\s+/g, '-')}`}
              >
                <Icon className="w-3 h-3" />{cta}
              </button>
            );
          })}
        </div>
      )}
    </div>
  );
};

const InsightCard = ({ item, isDark }) => {
  const tp = isDark ? 'text-white' : 'text-slate-800';
  const ts = isDark ? 'text-white/50' : 'text-slate-500';
  const border = isDark ? 'border-white/[0.06]' : 'border-black/[0.06]';
  const icons = { recommendation: Zap, alert: AlertTriangle, insight: TrendingUp };
  const colors = { recommendation: isDark ? 'text-indigo-400' : 'text-indigo-600', alert: isDark ? 'text-red-400' : 'text-red-500', insight: isDark ? 'text-emerald-400' : 'text-emerald-600' };
  const Icon = icons[item.type] || TrendingUp;
  return (
    <div className={`flex items-start gap-2 px-3 py-2 rounded-lg border ${border} ${isDark ? 'bg-[#0d1b2a]' : 'bg-[#f0ece4]'}`} data-testid={`churn-insight-${item.id}`}>
      <Icon className={`w-3.5 h-3.5 flex-shrink-0 mt-0.5 ${colors[item.type]}`} />
      <div className="flex-1 min-w-0">
        <p className={`text-[11px] leading-relaxed ${tp}`}>{item.text}</p>
        <p className={`text-[9px] mt-1 ${ts}`}>Zira · {item.time}</p>
      </div>
    </div>
  );
};

const V4SupportChurn = ({ isDark, onWorkflowToggle, workflowPanelOpen }) => {
  const [message, setMessage] = useState('');
  const [chatMode, setChatMode] = useState(false);
  const [chatMessages, setChatMessages] = useState([]);
  const messagesEndRef = useRef(null);

  const tp = isDark ? 'text-white' : 'text-slate-800';
  const ts = isDark ? 'text-white/50' : 'text-slate-500';
  const border = isDark ? 'border-white/[0.06]' : 'border-black/[0.06]';
  const solidBg = isDark ? 'bg-[#0a1628]' : 'bg-[#eee8df]';
  const inputBorder = isDark ? 'border-white/[0.08]' : 'border-black/[0.08]';
  const hoverBg = isDark ? 'hover:bg-white/[0.06]' : 'hover:bg-black/[0.04]';

  useEffect(() => { messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' }); }, [chatMessages]);

  const riskLevels = ['critical', 'high', 'medium'];
  const riskLabels = { critical: 'Critical', high: 'High', medium: 'Medium' };
  const riskHeaderColors = {
    critical: isDark ? 'text-red-400' : 'text-red-600',
    high: isDark ? 'text-amber-400' : 'text-amber-600',
    medium: isDark ? 'text-yellow-400' : 'text-yellow-600',
  };
  const riskHeaderBg = {
    critical: isDark ? 'bg-red-500/10 border-red-500/20' : 'bg-red-50 border-red-200',
    high: isDark ? 'bg-amber-500/10 border-amber-500/20' : 'bg-amber-50 border-amber-200',
    medium: isDark ? 'bg-yellow-500/10 border-yellow-500/20' : 'bg-yellow-50 border-yellow-200',
  };

  const accountsByRisk = {};
  riskLevels.forEach(r => { accountsByRisk[r] = CHURN_DATA.accounts.filter(a => a.risk === r); });

  const sendMessage = (text, resp) => {
    setChatMessages(prev => [...prev, { role: 'user', text }, { role: 'agent', text: resp?.text || CHURN_CHAT_RESPONSES.general.text }]);
    setChatMode(true);
  };
  const handleSend = () => { if (message.trim()) { sendMessage(message.trim()); setMessage(''); } };
  const handleAction = (account, cta) => {
    sendMessage(`${cta} for ${account.name}.`, { text: `I'll ${cta.toLowerCase()} for ${account.name} ($${(account.arr / 1000).toFixed(0)}K ARR, ${account.risk} risk).\n\nBased on the current health score of ${account.healthScore}/100, this is a timely action. I'll set this up and send you a confirmation.\n\nAnything else for this account?` });
  };

  const chatInput = (
    <div className="flex justify-center pb-4 px-4 flex-shrink-0">
      <div className="w-[80%]">
        <div className={`${solidBg} border ${inputBorder} rounded-2xl flex flex-col`}>
          <div className="px-4 pt-3 pb-2">
            <textarea value={message} onChange={(e) => setMessage(e.target.value)} onKeyDown={(e) => { if (e.key === 'Enter' && !e.shiftKey) { e.preventDefault(); handleSend(); } }}
              placeholder="Message Zira about churn risks..." rows={1} className={`w-full bg-transparent resize-none outline-none text-sm leading-5 ${tp} placeholder:${ts}`} style={{ height: 'auto', maxHeight: '200px' }} data-testid="churn-chat-input" />
          </div>
          <div className="flex items-center justify-between px-3 pb-3">
            <div className="flex items-center gap-1">
              <div className={`flex items-center gap-1.5 px-2 py-1.5 rounded-lg ${isDark ? 'bg-white/[0.04]' : 'bg-black/[0.03]'}`}>
                <img src={AGENT_THUMBS.zira} alt="" className="w-5 h-5 rounded-full object-cover" /><span className={`text-xs font-medium ${tp}`}>Zira</span>
              </div>
              <button onClick={onWorkflowToggle} className={`flex items-center gap-1.5 px-2 py-1.5 rounded-lg transition-colors ${workflowPanelOpen ? (isDark ? 'bg-white/[0.08]' : 'bg-black/[0.06]') : hoverBg}`}>
                <Zap className={`w-3.5 h-3.5 ${tp}`} /><span className={`text-xs font-medium ${tp}`}>Workflows</span>
              </button>
            </div>
            <div className="flex items-center gap-1.5">
              <button className={`w-8 h-8 rounded-xl flex items-center justify-center ${hoverBg}`}><Mic className={`w-4 h-4 ${ts}`} /></button>
              <button onClick={handleSend} className={`w-8 h-8 rounded-xl flex items-center justify-center ${message.trim() ? 'bg-gradient-to-br from-indigo-500 to-purple-600' : (isDark ? 'bg-white/[0.06]' : 'bg-black/[0.04]')}`} data-testid="churn-send-btn">
                <Send className={`w-3.5 h-3.5 ${message.trim() ? 'text-white' : ts}`} />
              </button>
            </div>
          </div>
        </div>
      </div>
    </div>
  );

  if (chatMode) {
    return (
      <div className="flex flex-col h-full" data-testid="churn-chat-view">
        <div className={`px-4 py-3 flex-shrink-0 border-b ${border}`}>
          <button onClick={() => setChatMode(false)} className={`flex items-center gap-2 px-2 py-1 rounded-lg ${hoverBg}`} data-testid="churn-back-btn">
            <ArrowLeft className={`w-4 h-4 ${ts}`} /><span className={`text-sm font-medium ${tp}`}>Back to Churn Risk</span>
          </button>
        </div>
        <div className="flex-1 overflow-y-auto"><div className="flex justify-center"><div className="w-[80%] py-5 space-y-5">
          {chatMessages.map((msg, i) => (
            <div key={i} className={msg.role === 'user' ? 'flex justify-end' : 'flex gap-3'}>
              {msg.role === 'agent' && <img src={AGENT_THUMBS.zira} alt="" className="w-7 h-7 rounded-full object-cover flex-shrink-0 mt-1" />}
              <div className={msg.role === 'user' ? `max-w-[75%] px-3.5 py-2.5 rounded-2xl rounded-br-md text-sm leading-relaxed ${isDark ? 'bg-indigo-500/15 text-white' : 'bg-indigo-500/10 text-slate-800'}` : `text-sm leading-relaxed whitespace-pre-line ${tp}`}>{msg.text}</div>
            </div>
          ))}
          <div ref={messagesEndRef} />
        </div></div></div>
        {chatInput}
      </div>
    );
  }

  return (
    <div className="flex flex-col h-full" data-testid="churn-page">
      <div className="px-5 pt-4 pb-3 flex-shrink-0">
        <div className="flex items-center gap-3">
          <ShieldAlert className={`w-5 h-5 ${isDark ? 'text-emerald-400' : 'text-emerald-600'}`} />
          <p className={`text-base font-semibold ${tp}`}>Churn Risk</p>
          <span className={`text-[10px] ${ts}`}>{CHURN_DATA.accounts.length} accounts</span>
        </div>
      </div>
      <div className={`border-t ${border}`} />

      <div className="flex-1 min-h-0 overflow-y-auto px-5 py-4 space-y-4">
        <div className="grid grid-cols-6 gap-3">
          {CHURN_DATA.metrics.map((m, i) => <MetricCard key={i} item={m} isDark={isDark} />)}
        </div>

        {/* Kanban columns by risk — with proper gap */}
        <div className="flex gap-4 min-h-0">
          {riskLevels.map(risk => {
            const accounts = accountsByRisk[risk] || [];
            const totalARR = accounts.reduce((sum, a) => sum + a.arr, 0);
            return (
              <div key={risk} className="flex-1 flex flex-col min-w-0">
                <div className="flex items-center justify-between mb-3">
                  <div className="flex items-center gap-2">
                    <span className={`text-[10px] font-semibold px-2 py-0.5 rounded-full border ${riskHeaderBg[risk]} ${riskHeaderColors[risk]}`}>{riskLabels[risk]}</span>
                    <span className={`text-[10px] ${ts}`}>{accounts.length}</span>
                  </div>
                  <span className={`text-[10px] font-medium ${tp}`}>${(totalARR / 1000).toFixed(0)}K ARR</span>
                </div>
                <div className="flex flex-col gap-4">
                  {accounts.map(a => <AccountCard key={a.id} account={a} isDark={isDark} onAction={handleAction} />)}
                  {accounts.length === 0 && <p className={`text-[10px] ${ts} text-center py-4`}>No accounts</p>}
                </div>
              </div>
            );
          })}
        </div>

        <div>
          <div className="flex items-center gap-2 mb-3">
            <img src={AGENT_THUMBS.zira} alt="" className="w-4 h-4 rounded-full object-cover" />
            <p className={`text-xs font-semibold ${tp}`}>Zira's Insights</p>
          </div>
          <div className="grid grid-cols-2 gap-2">
            {CHURN_DATA.insights.map(item => <InsightCard key={item.id} item={item} isDark={isDark} />)}
          </div>
        </div>
      </div>

      {chatInput}
    </div>
  );
};

export default V4SupportChurn;
